import { Crosshair, RotateCcw, Route, Spline, Trash2 } from "lucide-react";
import type { ReactNode } from "react";

import type { MeasureKey, Measures } from "../api/types";
import { Callout } from "../components/Callout";
import { PropRow } from "../components/panel";
import { Motion, Presence } from "../components/presence";
import { SaveDossierHint } from "../components/SaveDossierHint";
import { Badge, Button, FloatingPanel } from "../components/ui";
import { unitDesignation } from "../i18n/designation";
import { k, t, tServer } from "../i18n/i18n";
import { useDossierLanguage } from "../map/lineNames";
import { useManoeuvre } from "../map/manoeuvre/useManoeuvre";
import { useMeasureWrites } from "../map/manoeuvre/useMeasureWrites";
import { refuseWhileOrienting, useOrientation } from "../map/manoeuvre/useOrientation";
import { BOUNDARIES, type MeasureLevel, PHASED } from "../map/manoeuvre/zoMeasures";
import { entryZoom } from "../map/recenter";
import { useDeployment } from "../map/units/useDeployment";
import { useUi } from "../state/ui";
import { startEditing } from "../views/manoeuvre/finishEditing";
import { refuseCorridorWhilePreparing } from "../map/zones/zoneAttention";
import { useOpenZone } from "../map/zones/useZones";
import {
  lineWarnings,
  MEASURE_STATE_LABEL,
  measureAction,
  measureCenter,
  measureDepth,
  measureLevel,
  measureName,
  measurePosition,
  type MeasureRow,
  measureRows,
  movedPhases,
} from "../views/manoeuvre/manoeuvreModel";
import { CardActions, CardFooter, CardTitle } from "./cardParts";
import { refuseIfDossierLocked } from "../sharing/access";

/** Fiche d'une ligne de la ZO (docs/15 §4.4) : nom court et nom complet dans la langue du dossier, état (calculée en
 *  texte gris ; déplacée, retirée en pastille, R6), position (règle qui la place), profondeur, avertissements du
 *  serveur qui la concernent. Pied : « Centrer », « Revenir à la position doctrinale » (ligne déplacée ou retirée),
 *  « Retirer » (CFL, FSCL), « Modifier le fuseau » (limites arrière et avant, qui se déplacent avec le fuseau).
 *  La FLOT et la CFL sont lues au temps affiché : la fiche suit les touches 1, 2, 3. Dossier non enregistré : les
 *  écritures désignent « Enregistrer » (`ui.requestSave`, R12), avec l'avis du geste de la carte. */

/** Actions du pied d'une fiche, dans leur ordre. */
export type MeasureCardAction = "center" | "revert" | "remove" | "editCorridor";

/** Contenu de la fiche, sans React (testé par MeasureCard.test.tsx). */
export interface MeasureCardView {
  title: string;
  subtitle: string;
  /** État : libellé, et pastille (exception, R6) ou texte gris (état normal, sans objet). */
  state: { label: string; badge: boolean };
  position: string;
  depth: string | null;
  actions: MeasureCardAction[];
  /** Ligne mobile (limites internes, FLOT, CFL, FSCL) tracée au temps affiché : elle se glisse sur la carte. Une
   *  ligne retirée ou sans objet n'a rien à glisser. */
  movable: boolean;
  /** Avertissements du serveur qui concernent la ligne (français, à traduire par `tServer`). */
  warnings: string[];
}

export function measureCardView(
  row: MeasureRow,
  measures: Measures,
  level: MeasureLevel,
  options: { language: "fr" | "en"; editing: boolean; unitName?: (unit: string) => string | undefined },
): MeasureCardView {
  const name = measureName(row.names, options.language);
  const boundary = BOUNDARIES.includes(row.key);
  let label = t(MEASURE_STATE_LABEL[row.state]);
  if (row.state === "moved" && row.phase) label = t("Déplacée en {phase}", { phase: row.phase });
  const actions: MeasureCardAction[] = [];
  if (row.line && measureCenter(row.line)) actions.push("center");
  if (measureAction(row, "revert", options.language)) actions.push("revert");
  if (measureAction(row, "remove", options.language)) actions.push("remove");
  if (boundary && !options.editing) actions.push("editCorridor");
  return {
    title: name.short,
    subtitle: name.full,
    state: { label, badge: row.state === "moved" || row.state === "off" },
    position: measurePosition(row, measures, level, options.unitName),
    depth: row.line ? measureDepth(row.line, measures.axis_length_m) : null,
    actions,
    movable: !boundary && row.line !== null,
    warnings: lineWarnings(measures.warnings, row.key, row.phase),
  };
}

/** Ce que l'enregistrement du dossier permet, selon l'action bloquée (clés françaises, traduites à l'affichage). */
const MOVE_LINES = k("déplacer les lignes de la ZO");
const EDIT_CORRIDOR = k("modifier le fuseau");

export function MeasureCard({ measureKey }: { measureKey: MeasureKey }) {
  const { data } = useDeployment();
  const phase = useUi((s) => s.phase);
  const exerciseId = useUi((s) => s.exerciseId);
  const notify = useUi((s) => s.notify);
  const requestSave = useUi((s) => s.requestSave);
  const requestFlyTo = useUi((s) => s.requestFlyTo);
  const setView = useUi((s) => s.setView);
  const language = useDossierLanguage();
  const writes = useMeasureWrites();
  const editing = useManoeuvre((s) => s.editing);
  const orienting = useOrientation((s) => s.open);
  const { zone } = useOpenZone();
  // Retouche de la carte affichée mais pas encore enregistrée : l'écriture partirait de l'état d'avant le geste.
  const gesturePending = useManoeuvre((s) => s.working !== null);
  const measures = data?.corridor?.measures;
  const level = measureLevel(data?.level);
  if (!data || !measures || !level) return null;
  const row = measureRows(measures, level, phase).find((r) => r.key === measureKey);
  if (!row) return null;

  const unitName = (key: string) => {
    const unit = data.units.find((u) => u.key === key);
    return unit ? unitDesignation(unit.display_name || unit.designation) : undefined;
  };
  const view = measureCardView(row, measures, level, { language, editing, unitName });
  const busy = writes.busy || gesturePending;
  const unsaved = !writes.canEdit;
  // Mode orientation ouvert : les écritures de la fiche attendent qu'il soit appliqué ou annulé (« Appliquer »).
  const blocked = unsaved || orienting;
  const moved = PHASED.includes(row.key) ? movedPhases(measures, row.key).filter((p) => p !== row.phase) : [];

  // Dossier non enregistré : « Enregistrer » est désigné (`requestSave`, comme la fiche d'unité), avec l'avis du
  // geste de la carte (`useMeasureGestures.refuseNotSaved`) : même situation, même message.
  const designateSave = (action: string) => {
    if (refuseIfDossierLocked()) return; // lecture seule (docs/17 §3.2) : le bandeau, pas l'enregistrement
    requestSave();
    notify({
      text: exerciseId
        ? t("Enregistrez les modifications du dossier pour {action}", { action: t(action) })
        : t("Créez le dossier pour {action}", { action: t(action) }),
      detail: action === MOVE_LINES ? t("Les lignes de la ZO déplacées sont mémorisées dans le dossier d'exercice.") : undefined,
      tone: "info",
    });
  };
  const blockedLines = () => {
    if (!refuseWhileOrienting()) designateSave(MOVE_LINES);
  };
  const blockedCorridor = () => {
    if (!refuseWhileOrienting()) designateSave(EDIT_CORRIDOR);
  };

  const run = (action: "revert" | "remove") => {
    const write = measureAction(row, action, language);
    if (write) writes.setMeasure.mutate(write);
  };

  // Terrain libre en préparation : le fuseau ne se retouche qu'une fois le terrain prêt (carte de progression).
  const editCorridor = () => {
    if (refuseCorridorWhilePreparing(zone)) return;
    setView("manoeuvre");
    startEditing();
  };

  const center = row.line ? measureCenter(row.line) : null;
  const footer = view.actions.length ? (
    <CardFooter>
      {view.actions.includes("center") && center && (
        <Button variant="secondary" size="sm" onClick={() => requestFlyTo(center, entryZoom(data))}>
          <Crosshair size={14} /> {t("Centrer")}
        </Button>
      )}
      {view.actions.includes("revert") && (
        <Button variant="secondary" size="sm" disabled={busy} blocked={blocked} onBlocked={blockedLines} onClick={() => run("revert")}>
          <RotateCcw size={14} /> {t("Revenir à la position doctrinale")}
        </Button>
      )}
      {view.actions.includes("remove") && (
        <Button variant="danger-ghost" size="sm" disabled={busy} blocked={blocked} onBlocked={blockedLines} onClick={() => run("remove")}>
          <Trash2 size={14} /> {t("Retirer")}
        </Button>
      )}
      {view.actions.includes("editCorridor") && (
        <Button variant="secondary" size="sm" blocked={blocked} onBlocked={blockedCorridor} onClick={editCorridor}>
          <Route size={14} /> {t("Modifier le fuseau")}
        </Button>
      )}
    </CardFooter>
  ) : undefined;

  // Notes du bas : consigne de glissement (ligne tracée et mobile), limites qui suivent le fuseau, rappel
  // d'enregistrement s'il reste une écriture à faire, note de la FSCL. Ligne retirée ou sans objet : rien à glisser.
  const boundary = BOUNDARIES.includes(row.key);
  const writable = view.actions.some((action) => action !== "center");
  const notes: ReactNode[] = [];
  if (boundary) {
    notes.push(
      <p key="boundary" className="text-meta leading-snug text-ink-muted">
        {editing
          ? t("La limite arrière et la limite avant se déplacent avec le fuseau : saisissez-la sur la carte, la paire d'extrémité du fuseau suit.")
          : t("La limite arrière et la limite avant se déplacent avec le fuseau.")}
      </p>,
    );
    if (unsaved) notes.push(<SaveDossierHint key="save" action={EDIT_CORRIDOR} />);
  } else if (unsaved && (view.movable || writable)) {
    notes.push(<SaveDossierHint key="save" action={MOVE_LINES} />);
  } else if (view.movable) {
    notes.push(
      <p key="drag" className="text-meta leading-snug text-ink-muted">
        {row.phase
          ? t("Faites glisser la ligne le long du fuseau ; en mode édition, ses extrémités l'inclinent. La position vaut pour le temps affiché.")
          : t("Faites glisser la ligne le long du fuseau ; en mode édition, ses extrémités l'inclinent.")}
      </p>,
    );
  }
  if (row.key === "FSCL" && row.state !== "off")
    notes.push(
      <p key="fscl" className="text-meta leading-snug text-ink-muted">
        {t("Les Graphiques tactiques peuvent aussi porter une FSCL : retirez celle-ci pour éviter un doublon.")}
      </p>,
    );

  return (
    <FloatingPanel
      enter="right"
      width="card"
      tallHeader
      icon={Spline}
      title={<CardTitle title={view.title} subtitle={view.subtitle} />}
      actions={<CardActions screen="manoeuvre" topic="manoeuvre.mesures" />}
      footer={footer}
    >
      <div className="px-4 py-2">
        <PropRow label={t("État")}>
          {view.state.badge ? (
            <Badge dot tone="accent">
              {view.state.label}
            </Badge>
          ) : (
            <span className="text-ink-muted">{view.state.label}</span>
          )}
        </PropRow>
        {row.phase && <PropRow label={t("Temps")}>{row.phase}</PropRow>}
        <PropRow label={t("Position")}>{view.position}</PropRow>
        {view.depth && <PropRow label={t("Profondeur")}>{view.depth}</PropRow>}
        {moved.length > 0 && (
          <PropRow label={t("Déplacée aussi en")}>{moved.join(", ")}</PropRow>
        )}
      </div>
      <Presence show={view.warnings.length > 0}>
        <Motion origin="fade" className="space-y-1.5 px-4 pb-2">
          {view.warnings.map((warning) => (
            <Callout key={warning} tone="warn">
              {tServer(warning)}
            </Callout>
          ))}
        </Motion>
      </Presence>
      <Presence show={notes.length > 0}>
        <Motion origin="fade" className="space-y-1.5 border-t border-line px-4 py-3">
          {notes}
        </Motion>
      </Presence>
    </FloatingPanel>
  );
}
